/**
 * O que faz: define regras, identidade e limites da organização por coluna.
 * Onde usa: motor híbrido e configuração da aba Agentes.
 * Conexões: catálogo puro, worker e reexport do frontend; sem consultas.
 * Tabelas: contratos das configurações, regras e checkpoint, sem acesso direto.
 * Data: 20/10/2026.
 */
import type { LeadCategory, PedidoRetorno } from './contracts.ts';

export type CondicaoColuna = 'interesse' | 'preco' | 'quer_marcar' | 'retomar' | 'personalizado' | 'fato_sistema';
export type FrequenciaOrganizacao = 'diaria' | 'quatro_horas';
export type ExigenciaRegistro = 'venda' | 'agenda' | 'comparecimento' | null;
export type MarcoAnalitico = LeadCategory;
export interface ExemploColuna {
  texto: string;
  corresponde: boolean;
}
export interface RegraColuna {
  etapaId: string;
  nome: string;
  condicao: CondicaoColuna;
  descricao: string;
  exclusoes: string[];
  exemplos: ExemploColuna[];
  prioridade: number;
  origensPermitidas: string[];
  marcoAnalitico: MarcoAnalitico | null;
  exigeRegistro: ExigenciaRegistro;
  modo: 'suggestion' | 'automatic';
  confirmada: boolean;
}
export interface IdentidadeAnalise {
  clinicaId: string;
  leadId: string;
  // No motor revisado, são carimbos ISO dos sinais existentes, sem novos gatilhos.
  revisaoConversa: string;
  revisaoFatos: string;
  versaoRegras: string;
  revisaoForcada?: string;
  modeloJev: string;
  modeloLlm: string;
  versaoPerguntas: string;
}
export interface ConfiguracaoOrganizacao {
  motor: 'legacy' | 'hybrid';
  frequencia: FrequenciaOrganizacao;
  regrasVersao: string;
  regras: RegraColuna[];
}
export interface RascunhoOrganizacao {
  origem: 'sugerida' | 'existente';
  frequencia: FrequenciaOrganizacao;
  regras: RegraColuna[];
  versaoEsperada: string;
  novasColunas: Array<{ idTemporario: string; nome: string }>;
}
export interface UsoInferencia {
  inputTokens: number;
  outputTokens: number;
  costUsd: number | null;
  modelo: string;
}
export interface ChamadaInferencia<T> {
  resultado: T;
  uso: UsoInferencia;
}
export interface CortesOrganizacao {
  sugestao: number;
  automatico: number;
  margem: number;
  evidencia: number;
  retorno: number;
}
export interface ResultadoOrganizacao {
  // Metadados fechados explicam o resultado sem misturar confiança original com a aprovação final.
  diagnostico?: import('./organizacao-diagnostico.ts').DiagnosticoOrganizacao;
  etapaSugeridaId: string | null;
  marcoAnalitico: MarcoAnalitico | null;
  origem: 'deterministic' | 'jev' | 'llm';
  confianca: number | null;
  motivoCodigo:
    | 'fato_sistema'
    | 'criterio_coluna'
    | 'informacao_insuficiente'
    | 'contexto_incompleto'
    | 'precisa_revisao';
  retorno: PedidoRetorno | null;
  probabilidades: Record<string, number>;
  // Confirmação explícita distingue uma exceção comum da validação exigida por colunas de alto impacto.
  confirmadoLlm?: boolean;
  custoConhecido?: boolean;
  etapaOriginalJevId?: string | null;
}
export interface ContextoOrganizacao {
  mensagens: Array<{ de: 'paciente' | 'equipe'; texto: string }>;
  fatos: {
    hasSale: boolean;
    hasFutureAppointment: boolean;
    hasAttendedAppointment: boolean;
    hasActiveReturn: boolean;
  };
}
export interface SugestaoRegraLocal {
  condicao: CondicaoColuna | null;
  descricao: string;
  exigeRegistro: ExigenciaRegistro;
  precisaEsclarecimento: boolean;
}
// Limites compartilhados impedem que a tela aceite um rascunho que o servidor recusará.
export const LIMITES_ORGANIZACAO = {
  colunas: 30,
  descricao: 600,
  exclusoes: 5,
  exclusao: 200,
  exemplos: 6,
  exemplo: 300,
  lote: 50,
  concorrencia: 4,
  contextoCaracteres: 12000,
  mensagensIniciais: 20,
  mensagensRecentes: 80,
} as const;
